import React from 'react';
import { Card, Accordion, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import AddressSummary from './AddressSummary';
import Button from '../Parts/Button';
import { ExistingAddress } from './addressTypes';

type AddressSummaryBlockProps = {
	address: ExistingAddress;
	onEdit: () => void;
	onCopy?: () => void;
	showEdit: boolean;
	showCopy?: boolean;
	copyButtonText?: string;
	title: string;
	quoteType: string;
	isPropertyAddress?: boolean;
};

const AddressSummaryBlock = ({
	address,
	onEdit,
	onCopy,
	showEdit,
	showCopy,
	copyButtonText,
	title,
	quoteType,
	isPropertyAddress
}: AddressSummaryBlockProps) => {
	const humanize = (str) => {
		let i;
		const frags = str.split('_');
		for (i = 0; i < frags.length; i++) {
			frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
		}
		return frags.join(' ');
	};

	const checkMissing = (): string[] => {
		const installationAddressRequiredKeys: string[] = [
			'company_name',
			'address',
			'county_subdivision_id',
			'city',
			'zip',
		];

		const shippingAddressRequiredKeys: string[] = [
			'company_name',
			'first_name',
			'last_name',
			'is_residential',
			'lift_gate',
			'address',
			'county_subdivision_id',
			'city',
			'zip',
		];

		const requiredKeys
			= title === 'Installation Address'
				? installationAddressRequiredKeys
				: shippingAddressRequiredKeys;

		if (!address || Object.keys(address).length === 0) {
			return ['Missing all Fields'];
		}

		return requiredKeys.filter((key) => {
			const value = address[key];
			return value === null || value === '';
		});
	};

	const missingFields = checkMissing();
	const missing = missingFields.length > 0;

	const prettyMissingFields = missingFields.map((mf) => {
		const prettyMF = humanize(mf);
		return (
			<Col key={mf}>
				<span className="text-danger">{prettyMF}</span>
			</Col>
		);
	});

	return (
		<Accordion className={`h-100 local-bootstrap accordion-${quoteType.trim()}`}>
			<Card className="h-100">
				<Accordion.Item
					className="d-lg-none"
					eventKey={title}
				>
					<Accordion.Header>
						<Col xs={10}>
							<h4
								className={missing ? 'text-danger' : `text-${quoteType.trim()}`}
							>
								{title}
							</h4>
						</Col>
						<Col className="text-end" xs={1}>
							{missing ? (
								<FontAwesomeIcon
									size="lg"
									title="Missing required data"
									className="text-danger"
									icon={faExclamationTriangle}
								/>
							) : null}
						</Col>
					</Accordion.Header>
				</Accordion.Item>
				<Card.Header className="d-none d-lg-block">
					<Row className="mt-2">
						<Col xs={10}>
							<h4
								className={missing ? 'text-danger' : `text-${quoteType.trim()}`}
							>
								{title}
							</h4>
						</Col>
						<Col xs={2}>
							{missing ? (
								<FontAwesomeIcon
									size="lg"
									title="Missing required data"
									className="text-danger"
									icon={faExclamationTriangle}
								/>
							) : null}
						</Col>
					</Row>
				</Card.Header>
				<Accordion.Collapse eventKey={title} className="d-lg-block">
					<>
						<Row>
							<AddressSummary
								isPropertyAddress={isPropertyAddress ?? false}
								address={address}
							/>
						</Row>
						<Col className="mb-4 pb-4">
							<Row className={missing ? 'd-block' : 'd-none'}>
								<Col className="m-2">
									<Row>
										<Col>
											<h5 className="text-danger">Missing:</h5>
										</Col>
									</Row>
									<Row>{prettyMissingFields}</Row>
								</Col>
							</Row>
						</Col>
						{showEdit ? (
							<Row className="bottom-row mb-1">
								<Col className="text-end">
									<div className={`d-flex justify-content-${showCopy ? "between" : "end"} gap-2`}>
										{showCopy && onCopy ? (
											<Button
												className="mb-2"
												size="sm"
												color="gray"
												onClick={onCopy}
											>
												{copyButtonText || 'Copy Address'}
											</Button>
										) : null}
										<Button
											className="mb-2"
											size="sm"
											color={quoteType ?? 'green'}
											onClick={onEdit}
										>
											Edit
										</Button>
									</div>
								</Col>
							</Row>
						) : null}
					</>
				</Accordion.Collapse>
			</Card>
		</Accordion>
	);
};

export default AddressSummaryBlock;
